import { RegisterScreen } from '@/components/RegisterScreen';
import type { FormValues } from '@/components/ChildRecords';
import { formatDate, formatNumber, todayIso } from '@/components/live';
import { useRows } from '@/hooks/useResource';
import {
  area, dateNotFuture, doneByFields, plotCell, plotField, speciesBelongsToPlot, speciesField, usePracticePickers,
} from '@/screens/parts/practiceFields';
import {
  APPLICATION_METHODS, INPUT_CATEGORIES, INPUT_NATURES, INPUT_PURPOSES, RATE_UNITS, SAMPLES, optionLabel,
} from '@/lib/practiceLists';

interface Row {
  id: string; input_application_code: string | null; plot_code: string | null; farmer_name: string | null; village_name: string | null;
  applied_on: string; input_category: string; input_nature: string; product_name: string; active_ingredient: string | null;
  purpose: string; target_problem: string | null; application_rate: number; application_rate_unit: string;
  applied_by_display: string | null; species_label: string | null; fsc_hazardous: boolean; fsc_derogation_ref: string | null;
  ppe_used: boolean; area_treated_ha: number | null; pre_harvest_interval_days: number | null;
}

/**
 * Every fertilizer, pesticide, fungicide and weedicide applied, across every plot (owner,
 * 2026-09-25: "these all make as ui pages under Plot management").
 *
 * This is the one of the six an auditor reaches for first. FSC asks what highly hazardous
 * pesticides were used and under what derogation, and that question is about the certificate, not
 * about a plot - it cannot be answered one plot record at a time. The HHP count leads the stat
 * cards for the same reason.
 */
/**
 * The form follows the input type (owner, 2026-09-27: "recategorise and display the fields based on
 * selection? for fertilizer showing options associated with fertilizer and fields associated with
 * fertilizer and its relevant names"). NUTRITION feeds the crop; PROTECTION acts on a pest, disease
 * or weed. Each gets its own purposes, methods, units, names and sample values; the columns stored
 * are the same, so nothing already recorded changes.
 */
const NUTRITION_TYPES = ['fertilizer', 'manure_compost', 'soil_amendment', 'bio_stimulant', 'growth_regulator'];
const PROTECTION_TYPES = ['pesticide', 'insecticide', 'fungicide', 'herbicide'];
const isNutrition = (form: FormValues) => NUTRITION_TYPES.includes(String(form.input_category));
const isProtection = (form: FormValues) => PROTECTION_TYPES.includes(String(form.input_category));
/** The subset of `all` named by `ids`, or all of them when the type says nothing. */
const pickOptions = <O extends { id: string }>(all: O[], ids?: string[]) => (ids ? all.filter((option) => ids.includes(option.id)) : all);

const NUTRITION_PURPOSES = ['nutrition', 'soil_health', 'growth_promotion', 'other'];
const PURPOSES_FOR: Record<string, string[] | undefined> = {
  fertilizer: NUTRITION_PURPOSES, manure_compost: NUTRITION_PURPOSES, soil_amendment: ['soil_health', 'nutrition', 'other'],
  bio_stimulant: ['growth_promotion', 'soil_health', 'nutrition', 'other'], growth_regulator: ['growth_promotion', 'other'],
  pesticide: ['pest_control', 'disease_control', 'preventive', 'other'], insecticide: ['pest_control', 'preventive', 'other'],
  fungicide: ['disease_control', 'preventive', 'other'], herbicide: ['weed_control', 'other'],
};
const NUTRITION_METHODS = ['soil_broadcast', 'basal', 'ring_application', 'drip_fertigation', 'foliar_spray', 'other'];
const PROTECTION_METHODS = ['foliar_spray', 'drench', 'injection', 'seed_treatment', 'soil_broadcast', 'other'];
const NUTRITION_UNITS = ['kg_per_ha', 'kg_per_acre', 'kg_per_plant', 'g_per_plant', 'l_per_ha', 'g_per_litre'];
const PROTECTION_UNITS = ['l_per_ha', 'ml_per_ha', 'g_per_ha', 'ml_per_litre', 'g_per_litre', 'ml_per_plant'];
const PRODUCT_LABEL: Record<string, string> = {
  fertilizer: 'Fertilizer (product / grade)', manure_compost: 'Manure / compost', soil_amendment: 'Amendment',
  bio_stimulant: 'Bio-stimulant (product)', growth_regulator: 'Growth regulator (product)',
  pesticide: 'Pesticide (product / brand)', insecticide: 'Insecticide (product / brand)',
  fungicide: 'Fungicide (product / brand)', herbicide: 'Weedicide (product / brand)',
};
const TARGET_LABEL: Record<string, string> = {
  fertilizer: 'Deficiency addressed', manure_compost: 'Deficiency addressed', soil_amendment: 'Soil problem addressed',
  bio_stimulant: 'Deficiency addressed', growth_regulator: 'Growth problem addressed',
  pesticide: 'Target pest or disease', insecticide: 'Target pest', fungicide: 'Target disease', herbicide: 'Target weed',
};
const NUTRITION_PRODUCTS = ['Urea (46% N)', 'DAP (18:46:0)', 'SSP (16% P2O5)', 'MOP (60% K2O)', 'NPK 10:26:26', 'NPK 19:19:19',
  'Vermicompost', 'Farmyard manure (FYM)', 'Neem cake', 'Agricultural lime', 'Gypsum', 'Zinc sulphate'];
const PROTECTION_PRODUCTS = ['Neem oil 1500 ppm', 'Trichoderma viride 1% WP', 'Pseudomonas fluorescens', 'Beauveria bassiana',
  'Mancozeb 75% WP', 'Copper oxychloride 50% WP', 'Chlorpyriphos 20% EC', 'Imidacloprid 17.8% SL', 'Glyphosate 41% SL'];
const NUTRIENT_CONTENT = ['46-0-0', '18-46-0', '0-16-0', '0-0-60', '10-26-26', '19-19-19', 'Organic (FYM / compost)'];
const DEFICIENCIES = ['Nitrogen deficiency', 'Phosphorus deficiency', 'Potash deficiency', 'Zinc deficiency', 'Boron deficiency',
  'Soil acidity', 'Low organic carbon'];

export default function InputsScreen() {
  const pick = usePracticePickers();
  const hhp = useRows<{ id: string; active_ingredient: string }>('fsc_hhp_list', {});
  const hhpMatch = (ingredient: string) => {
    const text = ingredient.toLowerCase();
    return text
      ? (hhp.data ?? []).map((row) => row.active_ingredient.replace(/\s*\(.*\)\s*$/, '')).find((name) => text.includes(name.toLowerCase())) ?? null
      : null;
  };
  const cutoff = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);

  return (
    <RegisterScreen<Row>
      plotFilter
      addLabel="Add input" exportName="inputs-applied"
      orderBy={{ column: 'applied_on', ascending: false }}
      recordLabel={(row) => `input ${row.input_application_code ?? ''} - ${row.product_name} on ${row.plot_code ?? ''}`}
      screenKey="plot.inventory"
      searchColumns={['input_application_code', 'plot_code', 'farmer_name', 'product_name', 'active_ingredient', 'target_problem']}
      secondary={{ sum: (row) => row.area_treated_ha ?? 0, unit: 'ha', digits: 2 }}
      subtitle="Every input applied to a plot - chemical or bio - with its rate, its purpose and who applied it."
      table="ops.plot_input_applications" title="Inputs applied" view="plot_input_applications"
      columns={[
        { key: 'input_application_code', label: 'Ref', render: (row) => <strong>{row.input_application_code ?? '--'}</strong> },
        { key: 'plot_code', label: 'Plot', render: plotCell },
        { key: 'applied_on', label: 'Applied', render: (row) => formatDate(row.applied_on) },
        { key: 'product_name', label: 'Product', render: (row) => (
          <span className="cell-stack">
            <strong>{row.product_name}</strong>
            <small className="muted">{[optionLabel(INPUT_CATEGORIES, row.input_category), optionLabel(INPUT_NATURES, row.input_nature), row.active_ingredient].filter(Boolean).join(' · ')}</small>
          </span>
        ) },
        { key: 'purpose', label: 'Purpose', render: (row) => (
          <span className="cell-stack">
            <strong>{optionLabel(INPUT_PURPOSES, row.purpose)}</strong>
            <small className="muted">{row.target_problem ?? ''}</small>
          </span>
        ) },
        { key: 'application_rate', label: 'Rate', align: 'right',
          render: (row) => `${formatNumber(row.application_rate, 3)} ${optionLabel(RATE_UNITS, row.application_rate_unit)}` },
        { key: 'area_treated_ha', label: 'Area', align: 'right', render: (row) => area(row.area_treated_ha) },
        { key: 'applied_by_display', label: 'Applied by', render: (row) => row.applied_by_display ?? '--' },
        { key: 'fsc_hazardous', label: 'FSC', render: (row) => (
          row.fsc_hazardous
            ? <span className="cell-stack"><span className="badge badge-red">FSC HHP</span><small className="muted">{row.fsc_derogation_ref ?? 'no derogation ref.'}</small></span>
            : <span className="muted">--</span>
        ) },
      ]}
      stats={[
        { key: 'hhp', label: 'FSC highly hazardous', tone: 'error', match: (row) => row.fsc_hazardous },
        // NOT a "hazardous without a derogation reference" card: `plot_input_applications_check4`
        // makes that row impossible to save, so the card would count zero for ever and read as an
        // all-clear nobody earned. What is worth seeing is the ones still inside their pre-harvest
        // interval, where a harvest now would be the finding.
        { key: 'in_phi', label: 'Inside pre-harvest interval', tone: 'warn', match: (row) => (
          row.pre_harvest_interval_days != null
          && new Date(row.applied_on).getTime() + row.pre_harvest_interval_days * 86400000 >= Date.now()
        ) },
        { key: 'no_ppe', label: 'No protective equipment', tone: 'warn',
          match: (row) => row.input_nature === 'chemical' && !row.ppe_used },
        { key: 'bio', label: 'Bio inputs', tone: 'ok', match: (row) => row.input_nature === 'bio' },
        { key: 'recent', label: 'Applied in 30 days', tone: 'info', match: (row) => row.applied_on >= cutoff },
      ]}
      defaults={{ applied_on: todayIso(), applied_by_type: 'farmer', input_nature: 'chemical' }}
      fields={[
        plotField(pick.plots),
        { key: 'applied_on', label: 'Applied on', type: 'date', required: true, max: todayIso() },
        speciesField(pick, false),
        { key: 'input_category', label: 'Input type', type: 'select', options: INPUT_CATEGORIES, required: true,
          hint: 'The fields below follow the type: a fertilizer asks for its nutrients, a pesticide for its target.' },
        { key: 'input_nature', label: 'Chemical or bio', type: 'select', options: INPUT_NATURES, required: true },
        { key: 'product_name', label: 'Product / brand name', type: 'text', required: true,
          labelFor: (form) => PRODUCT_LABEL[String(form.input_category)] ?? 'Product / brand name',
          suggestionsFor: (form) => (isNutrition(form) ? NUTRITION_PRODUCTS : isProtection(form) ? PROTECTION_PRODUCTS : SAMPLES.inputProduct) },
        { key: 'active_ingredient', label: 'Active ingredient', type: 'text',
          labelFor: (form) => (isNutrition(form) ? 'Nutrient content (N-P-K)' : 'Active ingredient'),
          hintFor: (form) => (isNutrition(form) ? 'As on the bag: 46-0-0 for urea, 18-46-0 for DAP, or the organic content.' : undefined),
          suggestionsFor: (form) => (isNutrition(form) ? NUTRIENT_CONTENT : SAMPLES.activeIngredient) },
        { key: 'purpose', label: 'Purpose of application', section: 'Application', type: 'select', required: true,
          optionsFor: (form) => pickOptions(INPUT_PURPOSES, PURPOSES_FOR[String(form.input_category)]) },
        { key: 'target_problem', label: 'Target (pest, disease, weed, deficiency)', type: 'text',
          labelFor: (form) => TARGET_LABEL[String(form.input_category)] ?? 'Target (pest, disease, weed, deficiency)',
          suggestionsFor: (form) => (isNutrition(form) ? DEFICIENCIES : SAMPLES.pestDisease),
          required: (form) => ['pest_control', 'disease_control', 'weed_control'].includes(String(form.purpose)) },
        { key: 'application_rate', label: 'Rate of application', type: 'number', required: true,
          labelFor: (form) => (isNutrition(form) ? 'Dose' : 'Rate of application') },
        { key: 'application_rate_unit', label: 'Rate unit', type: 'select', required: true,
          optionsFor: (form) => pickOptions(RATE_UNITS, isNutrition(form) ? NUTRITION_UNITS : isProtection(form) ? PROTECTION_UNITS : undefined) },
        { key: 'application_method', label: 'Method', type: 'select',
          optionsFor: (form) => pickOptions(APPLICATION_METHODS, isNutrition(form) ? NUTRITION_METHODS : isProtection(form) ? PROTECTION_METHODS : undefined) },
        { key: 'area_treated_ha', label: 'Area treated (ha)', type: 'number' },
        { key: 'quantity_used', label: 'Total quantity used', type: 'number' },
        { key: 'quantity_unit_id', label: 'Quantity unit', type: 'select', options: pick.units,
          required: (form) => String(form.quantity_used ?? '').trim() !== '' },
        ...doneByFields('applied_by', pick.workers, pick.contractors).map((field, at) => (at === 0 ? { ...field, section: 'Applied by & safety' } : field)),
        { key: 'ppe_used', label: 'Protective equipment used', type: 'check', placeholder: 'PPE worn while applying',
          showIf: (form) => form.input_nature === 'chemical' || isProtection(form) },
        // A waiting period before harvest belongs to a crop protection product, not to a fertilizer.
        { key: 'pre_harvest_interval_days', label: 'Pre-harvest interval (days)', type: 'number', showIf: (form) => !isNutrition(form) },
        { key: 'manufacturer', label: 'Manufacturer', section: 'Product & FSC', type: 'text', suggestions: SAMPLES.manufacturer },
        { key: 'batch_no', label: 'Batch no.', type: 'text', placeholder: 'e.g. B2409-117' },
        { key: 'fsc_hazardous', label: 'On the FSC highly hazardous pesticides list', type: 'check', placeholder: 'Yes - FSC HHP', wide: true,
          hint: 'Set automatically when the active ingredient is on your organisation\'s FSC HHP list.',
          showIf: (form) => form.input_nature === 'chemical' && !isNutrition(form) },
        { key: 'fsc_derogation_ref', label: 'FSC derogation / risk assessment ref.', type: 'text', required: true, placeholder: 'e.g. ESRA-2026-03',
          showIf: (form) => form.input_nature === 'chemical' && !isNutrition(form) && Boolean(form.fsc_hazardous) },
        { key: 'document_id', label: 'Label, bill or spray record', type: 'file' },
        { key: 'remarks', label: 'Remarks', type: 'textarea' },
      ]}
      validate={(form) => ({
        species_inventory_id: speciesBelongsToPlot(pick, form),
        fsc_derogation_ref: form.input_nature === 'chemical' && !String(form.fsc_derogation_ref ?? '').trim() && hhpMatch(String(form.active_ingredient ?? ''))
          ? `${hhpMatch(String(form.active_ingredient ?? ''))} is on the FSC highly hazardous pesticides list: tick FSC HHP and give the derogation reference.` : undefined,
        applied_on: dateNotFuture(form, 'applied_on', 'The date'),
        application_rate: Number(form.application_rate) > 0 || !form.application_rate ? undefined : 'The rate must be more than 0.',
      })}
    />
  );
}
